import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Star, MessageSquare, Clock, BadgeCheck, Languages } from "lucide-react";
import { photoSrc } from "@shared/photoSrc";

export function TopLawyersSection({ topLawyers, onBook }) {
  return (
          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-14 lg:pb-20">
            <div className="flex items-end justify-between mb-6">
              <div>
                <span className="text-xs font-bold uppercase tracking-widest text-emerald-600 inline-flex items-center gap-2">
                  <span className="adv-pulse-dot" /> Online now
                </span>
                <h2 className="mt-2 text-3xl lg:text-4xl font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>Top Lawyers Available Now</h2>
              </div>
              <Link to="/lawyers" className="hidden md:inline text-sm font-bold text-[#0A2540] hover:text-[#D4AF37]">See all lawyers →</Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="top-lawyers-grid">
              {topLawyers.length === 0 && Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="bg-white border border-slate-200 rounded-2xl p-5 h-[320px] adv-skeleton" />
              ))}
              {topLawyers.map((l, idx) => {
                const initials = (l.name || "").split(" ").slice(-2).map((x) => x[0]).join("");
                const respondMins = [1, 2, 2, 3][idx % 4];
                return (
                  <div
                    key={l.id}
                    data-testid={`top-lawyer-${l.id}`}
                    className="group bg-white border border-slate-200 rounded-2xl p-5 hover:shadow-xl hover:-translate-y-1 transition-all relative"
                  >
                    <div className="absolute top-3 right-3 flex items-center gap-1.5 bg-emerald-50 text-emerald-700 px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider">
                      <span className="adv-pulse-dot" /> Online
                    </div>
                    <div className="flex flex-col items-center text-center pt-3">
                      {l.photo ? (
                        <img src={photoSrc(l.photo, "sm")} alt={l.name} className="h-16 w-16 rounded-full object-cover border-2 border-[#D4AF37]" />
                      ) : (
                        <div className="h-16 w-16 rounded-full adv-navy text-[#D4AF37] grid place-items-center font-bold text-lg border-2 border-[#D4AF37]">
                          {initials}
                        </div>
                      )}
                      <div className="mt-3 flex items-center gap-1">
                        <h3 className="font-bold text-[#0A2540] text-sm leading-tight" style={{ fontFamily: "Poppins" }}>{l.name}</h3>
                        {l.verified && <BadgeCheck size={12} className="text-[#D4AF37]" />}
                      </div>
                      <p className="text-[11px] text-slate-500 mt-0.5">{l.specialization}</p>
                      <div className="mt-2 flex flex-wrap justify-center items-center gap-1.5 text-[11px]">
                        <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-800 px-1.5 py-0.5 rounded font-bold">
                          <Star size={10} fill="currentColor" />{Number(l.rating || 0).toFixed(1)}
                        </span>
                        <span className="text-slate-500">({l.reviews_count || 0})</span>
                        <span className="text-slate-500">· {Number(l.experience || 0)}y</span>
                      </div>
                      <div className="mt-2 flex flex-wrap justify-center items-center gap-2 text-[11px] text-slate-500">
                        <span className="inline-flex items-center gap-1"><Clock size={10} className="text-emerald-600" /> Responds in ~{respondMins} min</span>
                      </div>
                      {l.languages?.length > 0 && (
                        <p className="mt-1 text-[11px] text-slate-500 inline-flex items-center gap-1 truncate max-w-full">
                          <Languages size={10} /> {l.languages.slice(0, 2).join(" · ")}
                        </p>
                      )}
                      <p className="mt-3 text-xl font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>
                        ₹{l.pricing?.video?.original ?? l.fees?.video ?? l.fee}<span className="text-[11px] font-medium text-slate-500"> /video · 15 min</span>
                      </p>
                      <Button
                        onClick={() => onBook(l)}
                        data-testid={`top-chat-${l.id}`}
                        className="mt-3 w-full rounded-full adv-btn-primary font-bold"
                      >
                        <MessageSquare size={14} className="mr-1.5" /> Chat Now
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
  );
}
